import { ChangeDetectionStrategy, Component, ComponentRef, OnInit, signal, Type, ViewChild } from '@angular/core';
import { NavigationEnd } from '@angular/router';
import { map, Observable, of, Subject, switchMap } from 'rxjs';
import { CoreAppComponent } from 'src/app/core/core-app.component.js';

import { UserDb } from 'lib/cosmos/user/userDb.js';
import { consoleHelper } from 'lib/helpers/console-helper.class.js';
import { stringHelper } from 'lib/helpers/string-helper.class.js';
import { TypeComponentDirective } from './type-component-directive.js';
import { TypeCoreComponent } from './type-core.component.js';
import {
	IPageMetadata,
	IResolvedRoute,
	IResolvedRouteAction,
	IRouteData,
	ISharedData,
	ITypeParentRoute,
	ITypeRouteResult,
	TypeMainPath
} from './type-route.models';
import { TypeRouteService } from './type-route.service.js';

@Component({
	templateUrl: './type.component.html',
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: false
})
export class TypeComponent extends CoreAppComponent implements OnInit {
	@ViewChild(TypeComponentDirective, { static: true }) componentTemplate!: TypeComponentDirective;

	public readonly result = signal<ITypeRouteResult | undefined>(undefined);
	private readonly _urlUpdate = new Subject<string>();
	private activeComponentRef?: ComponentRef<TypeCoreComponent>;

	constructor(private typeRouteService: TypeRouteService) {
		super();
	}

	ngOnInit(): void {
		this.subscribeToUrlUpdate();
		this._urlUpdate.next(this.dependencies.router.url);

		super.subscribeToObservable(
			this.dependencies.router.events.pipe(
				map((data) => {
					if (data instanceof NavigationEnd) {
						this._urlUpdate.next(data.url);
					}
				})
			)
		);
	}

	handleSharedDataUpdated(updatedSharedData: ISharedData<any>, reRenderComponent: boolean): void {
		this.updateSharedData(updatedSharedData, reRenderComponent);
	}

	private subscribeToUrlUpdate(): void {
		super.subscribeToObservable(
			this._urlUpdate.pipe(
				switchMap((url) => {
					return this.initRoute(url);
				}),
				map((result) => {
					// url switched
					this.result.set(result);
					this.dependencies.sharedService.updateMetadata(result?.pageMetadata);
				})
			)
		);
	}

	private initRoute(url: string): Observable<ITypeRouteResult | undefined> {
		let matchedTypeRoute: IResolvedRoute | undefined;
		const routeData = this.getRouteData(url);

		if (!routeData) {
			consoleHelper.logToConsole({
				type: 'Warning',
				message: 'Invalid route data',
				partA: url
			});
			return of(undefined);
		}

		for (const typeRoute of this.typeRouteService.registerRoutes()) {
			if (routeData.mainType === typeRoute.mainPath) {
				if (typeRoute.type.toLowerCase() === routeData.type) {
					for (const typeRouteAction of typeRoute.children) {
						if (typeRouteAction.actionPath.toLowerCase() === routeData.action.toLowerCase()) {
							//  matched route

							matchedTypeRoute = typeRoute;

							const hasPermissions = this.hasPermissionToViewComponent(routeData.mainType);

							if (!hasPermissions) {
								consoleHelper.logToConsole({
									type: 'Warning',
									message: `You '${
										this.authUser()?.email ?? 'anonymous'
									}' don't have permission to view page '${routeData.mainType}.${routeData.type}.${
										routeData.action
									}${routeData.primaryId ? `.${routeData.primaryId}` : ''}'`,
									partA: url
								});

								if (!this.isBrowserPlatform()) {
									this.dependencies.navigateService.loginPage().navigate();
								}
								this.dependencies.navigateService.insufficientPermissionsPage().navigate();
								return of(undefined);
							} else {
								return this.initPageAndComponent(routeData, typeRoute, typeRouteAction);
							}
						}
					}
				}
			}
		}

		if (!matchedTypeRoute) {
			consoleHelper.logToConsole({
				type: 'Warning',
				message: 'No type route matched',
				data: routeData
			});

			this.dependencies.navigateService.notFoundPage().navigate();
		}

		return of(undefined);
	}

	protected updateSharedData(newSharedData: ISharedData<any>, reRenderComponent: boolean): void {
		const result = this.result();

		if (result) {
			const updatedMetadata = result.typeRouteAction?.getMetadata({
				sharedData: newSharedData,
				dependencies: this.dependencies
			});
			const updatedResult: ITypeRouteResult = {
				...result,
				sharedData: newSharedData,
				pageMetadata: updatedMetadata,
				parentRoutes: this.getParentRoutes(
					result.routeData,
					result.typeRoute,
					result.typeRouteAction,
					updatedMetadata,
					newSharedData
				)
			};

			this.result.set(updatedResult);
			this.dependencies.sharedService.updateMetadata(updatedMetadata);

			if (reRenderComponent) {
				// re-render component with updated shared data
				this.renderComponent(result.typeRouteAction.component, updatedResult.sharedData);
			}
		}
	}

	private loadSharedData(data: {
		typeRoute: IResolvedRoute;
		routeData: IRouteData;
		authUser: UserDb.IAuthUser;
		currentResult: ITypeRouteResult | undefined;
	}): Observable<ISharedData<any> | undefined> {
		// check if route has the same hash and do not reload
		// if the new / old routes share the same hash
		// useful e.g. for trainer client workout / diet routes as
		// there is no need to reload full user when selecting workout / diet
		if (data.typeRoute.sharedDataHash) {
			const sharedDataHashValue = data.typeRoute.sharedDataHash(data.routeData);
			if (sharedDataHashValue === data.currentResult?.sharedDataHash) {
				// no need to reload shared data using loadSharedData callback (which might make network call to API route)
				// reuse existing sharedData instead
				if (data.currentResult.sharedData) {
					return of(data.currentResult.sharedData);
				}
			}
		}

		return data.typeRoute.loadSharedData({
			routeData: data.routeData,
			authUser: data.authUser,
			dependencies: this.dependencies
		});
	}

	private initPageAndComponent(
		routeData: IRouteData,
		typeRoute: IResolvedRoute,
		typeRouteAction: IResolvedRouteAction
	): Observable<ITypeRouteResult | undefined> {
		const authUser = this.authUser();

		if (!authUser) {
			consoleHelper.logToConsole({
				type: 'Warning',
				message: `Auth user is required to access this page`,
				data: routeData
			});

			this.dependencies.navigateService.loginPage().navigate();
			return of(undefined);
		}

		if (this.shouldReloadSharedData(routeData)) {
			// clear existing component
			this.clearComponent();

			// set intermediate result (before shared data loads)
			// this makes UI feel "fast" because the old component
			// is disposed of immediately and new data appears from new component
			const intermediateResult = this.getTypeRouteResult({
				routeData: routeData,
				typeRoute: typeRoute,
				typeRouteAction: typeRouteAction,
				sharedData: undefined
			});

			const currentResult = this.result();

			// set intermediate result
			this.result.set(intermediateResult);

			// load shared data
			return this.loadSharedData({
				routeData: routeData,
				authUser: authUser,
				typeRoute: typeRoute,
				currentResult: currentResult
			}).pipe(
				map((data) => {
					const newResult = this.getTypeRouteResult({
						routeData: routeData,
						typeRoute: typeRoute,
						typeRouteAction: typeRouteAction,
						sharedData: this.getSharedData(data, typeRouteAction)
					});

					this.renderComponent(typeRouteAction.component, newResult.sharedData);

					return newResult;
				})
			);
		}

		const result = this.result();

		// reuse previous type data but update metadata & parent routes
		const updatedResult = this.getTypeRouteResult({
			routeData: routeData,
			typeRoute: typeRoute,
			typeRouteAction: typeRouteAction,
			sharedData: this.getSharedData(result?.sharedData, typeRouteAction)
		});

		this.renderComponent(typeRouteAction.component, updatedResult.sharedData);
		return of(updatedResult);
	}

	private getTypeRouteResult(config: {
		routeData: IRouteData;
		typeRoute: IResolvedRoute;
		typeRouteAction: IResolvedRouteAction;
		sharedData: any | undefined;
	}): ITypeRouteResult {
		const metadata = config.typeRouteAction.getMetadata({
			sharedData: config.sharedData,
			dependencies: this.dependencies
		});

		const newResult: ITypeRouteResult = {
			sharedData: config.sharedData,
			sharedDataHash: config.typeRoute.sharedDataHash?.(config.routeData),
			pageMetadata: metadata,
			routeData: config.routeData,
			typeRoute: config.typeRoute,
			layoutSize: config.typeRouteAction.layoutSize ?? 'large',
			typeRouteAction: config.typeRouteAction,
			parentRoutes: this.getParentRoutes(
				config.routeData,
				config.typeRoute,
				config.typeRouteAction,
				metadata,
				config.sharedData
			),
			menuItems: this.dependencies.layoutService.getMenuItemsWithState(
				config.typeRoute.menuItems({
					data: config.sharedData,
					routeData: config.routeData,
					dependencies: this.dependencies
				})
			),
			updateSharedData: (newSharedData) => {
				this.updateSharedData(newSharedData, false);
			}
		};
		return newResult;
	}

	private getSharedData(
		sharedData: ISharedData<any> | undefined,
		action: IResolvedRouteAction
	): ISharedData<any> | undefined {
		if (action.standaloneSharedData) {
			return action.standaloneSharedData({
				dependencies: this.dependencies
			});
		}

		if (!sharedData) {
			return sharedData;
		}
		if (!action.processSharedData) {
			return sharedData;
		}

		return action.processSharedData({
			sharedData: sharedData,
			dependencies: this.dependencies
		});
	}

	private getParentRoutes(
		routeData: IRouteData,
		typeRoute: IResolvedRoute,
		typeRouteAction: IResolvedRouteAction,
		metadata: IPageMetadata,
		sharedData: ISharedData<any>
	): ITypeParentRoute[] {
		const parentRoutes: ITypeParentRoute[] = [];

		if (routeData.mainType === 'trainer') {
			/* empty */
		} else if (routeData.mainType === 'client') {
			/* empty */
		} else if (routeData.mainType === 'branding') {
			/* empty */
		} else if (routeData.mainType === 'profile') {
			parentRoutes.push({
				routerLink: this.dependencies.navigateService
					.actionRoute((routes) => routes.profileRoutes.view.children.edit)
					.getUrl(),
				title: `Profile`
			});
		} else if (routeData.mainType === 'chat') {
			parentRoutes.push({
				routerLink: this.dependencies.navigateService
					.actionRoute((routes) => routes.chatRoutes.overview.children.list)
					.getUrl(),
				title: `Chat`
			});
		} else if (routeData.mainType === 'admin') {
			parentRoutes.push({
				routerLink: this.dependencies.navigateService
					.actionRoute((routes) => {
						return routes.adminLogRoutes.logs.children.listLogs;
					})
					.getUrl(),
				title: `Admin`
			});
		} else if (routeData.mainType === 'calendar') {
			parentRoutes.push({
				routerLink: this.dependencies.navigateService
					.actionRoute((routes) => routes.calendarRoutes.view.children.upcoming)
					.getUrl(),
				title: `Calendar`
			});
		}

		parentRoutes.push(
			...typeRoute.parentRoutes({
				dependencies: this.dependencies,
				routeData: routeData,
				user: this.authUser(),
				sharedData: sharedData
			})
		);

		if (typeRouteAction.parentRoutes) {
			parentRoutes.push(
				...typeRouteAction.parentRoutes({
					dependencies: this.dependencies,
					routeData: routeData,
					user: this.authUser(),
					sharedData: sharedData
				})
			);
		}

		parentRoutes.push({
			routerLink: this.dependencies.router.url,
			title: metadata.title
		});

		return parentRoutes;
	}

	private getRouteData(url: string): IRouteData {
		const routeSegments: string[] = url
			.split('/')
			.filter((m) => m.length)
			.map((m) => m.toLowerCase());

		// remove query string for action matching
		const action: string = stringHelper.extractStringBeforeQuestionMark(routeSegments?.[3] ?? '');

		return {
			mainType: this.getMainType(routeSegments[1]),
			type: routeSegments?.[2],
			action: action,
			primaryId: routeSegments?.[4] ?? undefined,
			secondaryId: routeSegments?.[5] ?? undefined,
			tertiaryId: routeSegments?.[6] ?? undefined
		};
	}

	private renderComponent(component: Type<any>, sharedData: ISharedData<any>): void {
		const typeComponentDirective = this.componentTemplate;

		if (!typeComponentDirective) {
			return;
		}

		// clear & destroy component
		this.clearComponent();

		// create component
		const createdComponent = typeComponentDirective.viewContainerRef.createComponent<TypeCoreComponent>(component);

		// set input data
		createdComponent.setInput('inputData', sharedData);

		// subscribe to updates to shared data
		createdComponent.instance.updateSharedDataOutput.subscribe((updatedSharedData) => {
			this.updateSharedData(updatedSharedData, false);
		});
	}

	private clearComponent(): void {
		// destroy active component
		this.activeComponentRef?.destroy();

		// clear existing component
		this.componentTemplate.viewContainerRef.clear();
	}

	private getMainType(mainType: string): TypeMainPath {
		if (mainType.toLowerCase() === 'trainer') {
			return 'trainer';
		}
		if (mainType.toLowerCase() === 'client') {
			return 'client';
		}
		if (mainType.toLowerCase() === 'profile') {
			return 'profile';
		}
		if (mainType.toLowerCase() === 'admin') {
			return 'admin';
		}
		if (mainType.toLowerCase() === 'chat') {
			return 'chat';
		}
		if (mainType.toLowerCase() === 'branding') {
			return 'branding';
		}
		if (mainType.toLowerCase() === 'calendar') {
			return 'calendar';
		}
		if (mainType.toLowerCase() === 'helpdesk') {
			return 'helpdesk';
		}
		return 'n/a';
	}

	private shouldReloadSharedData(newRouteData: IRouteData): boolean {
		const result = this.result();

		if (!result?.routeData) {
			return true;
		}

		if (result?.routeData.primaryId !== newRouteData.primaryId) {
			return true;
		}

		if (result?.routeData.type !== newRouteData.type) {
			return true;
		}

		if (result?.routeData.mainType !== newRouteData.mainType) {
			return true;
		}

		return false;
	}

	private hasPermissionToViewComponent(route: TypeMainPath): boolean {
		const authUser = this.authUser();

		if (!authUser) {
			return false;
		}

		if ((route === 'trainer' || route === 'branding') && authUser.userType === 'trainer') {
			return true;
		}

		if (route === 'client' && authUser.userType === 'client') {
			return true;
		}

		if (route === 'admin' && authUser.privilege === 'admin') {
			return true;
		}

		if (route === 'profile' || route === 'chat' || route === 'calendar' || route === 'helpdesk') {
			return true;
		}

		return false;
	}
}
